import getWorkflowRuns from "features/adminDashboard/actions/update/update.getWorkflowRuns";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "services/redux/rootReducer";
import styled from "styled-components";

const WORKFLOW_FETCH_FREQUENCY = 10000;

interface WorkflowRun {
    id: number;
    created_at: string;
    updated_at: string;
    status: string;
    conclusion: string | null;
    html_url: string;
}

interface WorkflowRunsResponse {
    workflow_runs: WorkflowRun[];
}

interface LogContainerProps {
    refreshKey: number;
}

const getErrorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : "Unable to load deployment logs.";

const LogContainer: React.FC<LogContainerProps> = ({ refreshKey }) => {
    const dispatch = useDispatch();
    const [logs, setLogs] = useState<WorkflowRun[]>([]);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [retryKey, setRetryKey] = useState<number>(0);

    const displayedLogs = useMemo(
        () =>
            [...logs]
                .sort(
                    (a, b) =>
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime()
                )
                .map((workflowRun) => {
                    const status =
                        workflowRun.conclusion || workflowRun.status;
                    return (
                        <LogEntry
                            href={workflowRun.html_url}
                            target="_blank"
                            rel="noreferrer"
                            $status={status}
                            key={workflowRun.id}
                            title="Open this deployment run in GitHub"
                        >
                            <span>
                                {new Date(
                                    workflowRun.created_at
                                ).toLocaleString()}
                            </span>
                            <span>{status.replace(/_/g, " ")}</span>
                        </LogEntry>
                    );
                }),
        [logs]
    );

    useEffect(() => {
        let cancelled = false;
        let timeout: ReturnType<typeof setTimeout> | undefined;

        const fetchWorkflows = async () => {
            if (!cancelled) setIsRefreshing(true);

            try {
                const response = (await dispatch(
                    getWorkflowRuns()
                )) as WorkflowRunsResponse;
                if (cancelled) return;

                setLogs(response?.workflow_runs || []);
                setError("");
            } catch (requestError) {
                if (cancelled) return;
                setError(getErrorMessage(requestError));
            } finally {
                if (!cancelled) {
                    setIsRefreshing(false);
                    timeout = setTimeout(
                        fetchWorkflows,
                        WORKFLOW_FETCH_FREQUENCY
                    );
                }
            }
        };

        fetchWorkflows();

        return () => {
            cancelled = true;
            if (timeout) clearTimeout(timeout);
        };
    }, [dispatch, refreshKey, retryKey]);

    return (
        <Container>
            <HeaderTab>
                <span>Recent deployments</span>
                <span>{isRefreshing ? "Refreshing..." : "Auto refresh"}</span>
            </HeaderTab>
            <Logs aria-live="polite">
                {error && (
                    <ErrorMessage role="alert">
                        <span>{error}</span>
                        <RetryButton
                            type="button"
                            onClick={() =>
                                setRetryKey((currentKey) => currentKey + 1)
                            }
                        >
                            Retry
                        </RetryButton>
                    </ErrorMessage>
                )}
                {!error && isRefreshing && logs.length === 0 && (
                    <EmptyMessage>Loading deployment logs...</EmptyMessage>
                )}
                {!error && !isRefreshing && logs.length === 0 && (
                    <EmptyMessage>No deployments found.</EmptyMessage>
                )}
                {displayedLogs}
            </Logs>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-flow: column;
    box-shadow: 1px 0px 10px 1px rgba(0, 26, 255, 0.1);

    width: 80%;
    min-height: 11rem;

    & > * {
        width: 100%;

        &:first-child {
            flex: 1;
            border-radius: 0.2rem 0.2rem 0 0;
        }
        &:last-child {
            flex: 4 1 auto;
            overflow-y: auto;
            height: 0px;
            border-radius: 0 0 0.2rem 0.2rem;
        }
    }

    @media (max-width: 768px) {
        min-height: 12rem;
        width: 95%;
    }
`;

const HeaderTab = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    min-height: 2.25rem;
    padding: 0.45rem 0.75rem;
    background: ${(props) => props.theme.colors.primary[50]};
    color: ${(props) => props.theme.colors.primary[800]};
    font-size: 0.8rem;

    & > *:first-child {
        font-weight: ${(props) => props.theme.fontWeight.bolder};
    }

    & > *:last-child {
        color: ${(props) => props.theme.colors.neutral[600]};
    }
`;

const Logs = styled.div`
    box-sizing: border-box;
    background: ${(props) => props.theme.colors.neutral[50]};
    color: ${(props) => props.theme.colors.primary[100]};
    padding: 0.25rem 0.75rem;

    display: flex;
    flex-flow: column;
`;

const LogEntry = styled.a<{ $status: string }>`
    &:not(:first-of-type) {
        opacity: 0.55;
    }

    padding: 0.5rem 0;
    border-bottom: solid 0.05rem ${(props) => props.theme.colors.primary[50]};
    color: inherit;
    text-decoration: none;

    & > *:last-child {
        font-weight: ${(props) => props.theme.fontWeight.bolder};
        font-size: 0.9em;
        text-transform: capitalize;
        color: ${(props) =>
            props.theme.colors.deploymentStatus[props.$status] ||
            props.theme.colors.primary[100]};
    }

    &:hover,
    &:focus-visible {
        opacity: 1;
        background: ${(props) => props.theme.colors.neutral[135]};
    }

    user-select: none;

    display: flex;
    justify-content: space-between;
    gap: 1rem;
`;

const EmptyMessage = styled.div`
    margin: auto;
    padding: 1.5rem;
    color: ${(props) => props.theme.colors.neutral[600]};
    text-align: center;
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin: 0.5rem 0;
    padding: 0.75rem;
    border-radius: 0.2rem;
    background: ${(props) => props.theme.colors.danger[100]};
    color: ${(props) => props.theme.colors.danger[700]};
`;

const RetryButton = styled.button`
    border: 1px solid ${(props) => props.theme.colors.danger[400]};
    border-radius: 0.2rem;
    padding: 0.35rem 0.65rem;
    background: ${(props) => props.theme.colors.neutral[50]};
    color: ${(props) => props.theme.colors.danger[700]};
    cursor: pointer;
`;

export default LogContainer;
